import type { ReactNode } from 'react'
import type { ProviderAccount } from '@domain/ports/finance-repository'

/**
 * Os campos crus das três telas de cadastro da Task 11.
 *
 * **Feio de propósito** — a UI de verdade é a Fase 4. O que estes componentes
 * entregam não é aparência, é o par `<label for>`/`id` em todo campo: sem ele o
 * teste teria de procurar `<input>` por posição, e um teste que encontra o campo
 * pela ordem em que ele está na tela passa a aprovar a tela errada assim que
 * alguém trocar dois campos de lugar.
 *
 * Vários componentes num arquivo só é o que o `react-refresh` permite (o que
 * ele proíbe é exportar **outra coisa** ao lado de um componente); um arquivo
 * por campo seria três arquivos de dez linhas.
 */

type FieldProps = {
  id: string
  label: string
  children: ReactNode
  /** Texto de erro ou de ajuda, abaixo do campo. */
  hint?: string | null
}

const CONTROL =
  'w-full rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] px-2 py-1 text-sm'

export function Field({ id, label, children, hint }: FieldProps) {
  return (
    <p className="mb-2">
      <label htmlFor={id} className="block text-xs text-[var(--text-muted)]">
        {label}
      </label>
      {children}
      {hint !== undefined && hint !== null && (
        <span className="block text-xs text-[var(--negative)]">{hint}</span>
      )}
    </p>
  )
}

type TextFieldProps = {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  hint?: string | null
  placeholder?: string
}

export function TextField({ id, label, value, onChange, hint, placeholder }: TextFieldProps) {
  return (
    <Field id={id} label={label} hint={hint}>
      <input
        id={id}
        type="text"
        className={CONTROL}
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      />
    </Field>
  )
}

type Option = { value: string; label: string }

type SelectFieldProps = {
  id: string
  label: string
  value: string
  options: Option[]
  onChange: (value: string) => void
  hint?: string | null
}

export function SelectField({ id, label, value, options, onChange, hint }: SelectFieldProps) {
  return (
    <Field id={id} label={label} hint={hint}>
      <select
        id={id}
        className={CONTROL}
        value={value}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  )
}

/**
 * A falha de uma escrita, visível. `role="alert"` porque é o resultado de uma
 * ação que a pessoa acabou de tomar — e porque é assim que o teste a encontra
 * sem depender do texto.
 */
export function Problem({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="mb-2 text-sm text-[var(--negative)]">
      {children}
    </p>
  )
}

/**
 * O seletor de vínculo com o provider, compartilhado pelas telas de conta e de
 * cartão.
 *
 * ## Por que ele existe
 *
 * A ingestão só grava transação de uma conta externa que aponte para um
 * instrumento nosso (decisão 132). Sem esta lista, vincular exigiria copiar um
 * UUID do dashboard da Pluggy à mão — e errar um caractere daria um vínculo que
 * parece feito e nunca ingere nada.
 *
 * ## De onde vem a lista
 *
 * De `providerAccounts`, que o sync publica a cada execução. **O cliente lê e
 * não escreve** (`firestore.rules`): inventar aqui uma conta que a Pluggy não
 * tem faria a tela mostrar tudo vinculado enquanto a ingestão seguisse dizendo
 * "sem instrumento".
 *
 * ## O filtro por `kind` não é cosmético
 *
 * Vincular um cartão a uma conta bancária inverteria o **sinal** de todas as
 * transações dele: o adapter nega o valor quando a conta é de crédito (decisão
 * 124a), e é o instrumento que decide isso. Toda compra viraria receita. Por
 * isso a tela de conta só oferece `kind: 'account'`, e a de cartão só
 * `creditCard`.
 */
export function ProviderLinkField({
  kind,
  value,
  options,
  onChange,
  label,
}: {
  kind: ProviderAccount['kind']
  value: string | null
  options: ProviderAccount[]
  onChange: (externalId: string | null) => void
  label: string
}) {
  const doTipo = options.filter((o) => o.kind === kind)
  if (doTipo.length === 0) return null

  return (
    <label className="flex items-baseline gap-1">
      <span className="text-[var(--text-muted)]">Pluggy:</span>
      <select
        aria-label={label}
        className="border-b border-[var(--border)] bg-transparent"
        value={value ?? ''}
        onChange={(event) => {
          onChange(event.target.value === '' ? null : event.target.value)
        }}
      >
        <option value="">não vinculado</option>
        {doTipo.map((o) => (
          <option key={o.externalId} value={o.externalId}>
            {o.lastFourDigits === null ? o.name : `${o.name} · final ${o.lastFourDigits}`}
          </option>
        ))}
      </select>
    </label>
  )
}
